/**
 * Budets vilkår — de tjueto feltene, og standardtekstene de arver fra.
 *
 * ---------------------------------------------------------------------------------------------
 * ET BUD ER ET UTKAST TIL KONTRAKTEN, og det er hele designet.
 *
 * Atle, 4. september: «Disse feltene må også da følges i performance på artistens kontrakt.»
 *
 * `performance_contracts` hadde allerede honorar, billing, gjestelisteplasser, settlengde,
 * merchandise og eksklusivitetsradius. Budet fikk derfor NØYAKTIG de samme navnene på det den
 * manglet — de seks ytelsene og de fire tekstene — slik at overføringen fra bud til kontrakt er en
 * kopi og ikke en oversettelse. To sett navn for det samme er to sett som glir fra hverandre.
 *
 * ---------------------------------------------------------------------------------------------
 * ET BUD KREVER AT ARTISTEN ER PLASSERT. EN FORESPØRSEL GJØR DET IKKE.
 *
 * «Ønsker man å legge inn et konkret bud må artisten være tilknyttet et arrangement eller venue.
 * Det vil si den må være lagt inn på programmering på en festival eller venue for å legge til bud.»
 *
 * Grunnen er ikke formell: et bud handler om en DATO, en SCENE, en KAPASITET og et KLOKKESLETT, og
 * ingen av dem finnes før artisten står på et brett. En forespørsel spør om pris og tilgjengelighet
 * og trenger ingen av delene — det er spørsmålet man stiller FØR man har bestemt seg.
 *
 * ---------------------------------------------------------------------------------------------
 * SEKS YTELSER MED EN PART, IKKE SEKS TEKSTFELTER.
 *
 * «Noen av feltene som overnatting og transport må ha logikk med seg slik at vi kan bruke det i
 * festivalen senere. Her kan det ikke bare være tekst input.»
 *
 * Så hver av dem har en PART — hvem som dekker den — og en presisering ved siden av. Parten er det
 * advancen skal kunne regne på; teksten er det «arrangør dekker standard festivalovernatting for
 * reisefølget» sier, og som ingen enum kan bære.
 */
import { z } from 'zod';

import { MAX_COST_AMOUNT_MINOR } from './economy.js';

/**
 * Hvem som dekker en ytelse.
 *
 * `not_agreed` ER IKKE DET SAMME SOM `artist`, og forskjellen er hele grunnen til at feltet
 * finnes: «ingen har bestemt» er en tilstand man må kunne se, ikke en man skal gjette seg bort fra.
 * `shared` finnes fordi delt reise er en helt vanlig avtale.
 */
export const PROVISION_PARTIES = ['organiser', 'artist', 'shared', 'not_agreed'] as const;
export const provisionPartySchema = z.enum(PROVISION_PARTIES).meta({ id: 'ProvisionParty' });
export type ProvisionParty = (typeof PROVISION_PARTIES)[number];

/** De seks ytelsene, i den rekkefølgen de står i budet. */
export const PROVISIONS = [
  'accommodation',
  'catering',
  'travel',
  'localTransport',
  'backline',
  'production',
] as const;
export type Provision = (typeof PROVISIONS)[number];

/**
 * Eksklusivitet: en radius eller en setning.
 *
 * Atle: «default er den en slider med km, men man kan velge å legge inn tekst i stedet.» Modusen
 * avgjør hvilken av de to som GJELDER, så en radius man har byttet bort fra ikke blir stående og
 * bety noe i en kontrakt.
 */
export const EXCLUSIVITY_MODES = ['none', 'radius', 'text'] as const;
export const exclusivityModeSchema = z.enum(EXCLUSIVITY_MODES).meta({ id: 'ExclusivityMode' });
export type ExclusivityMode = (typeof EXCLUSIVITY_MODES)[number];

/** En vilkårstekst. Én til to setninger — en rider hører hjemme som et vedlegg. */
export const MAX_TERM_TEXT = 1000;

/**
 * Vilkårspunktet nederst. Fire tusen tegn: det er et AVSNITT og ikke en setning, og
 * standardteksten alene er tusen.
 */
export const MAX_CONTRACT_TERMS_TEXT = 4000;

/**
 * De to plassholderne malen kan bruke.
 *
 * Doble krøllparenteser fordi det er formen i18next bruker overalt ellers i produktet — en
 * plassholder som ser ut som alle andre er en plassholder ingen lurer på.
 */
export const CONTRACT_TERMS_TOKENS = { artist: '{{artist}}', organiser: '{{organiser}}' } as const;

/**
 * Malen med navnene satt inn.
 *
 * Én funksjon, i kontraktpakken, fordi BÅDE apiet (når budet lages) og nettsiden (når man
 * forhåndsviser standarden i innstillingene) må gjøre nøyaktig det samme. To kopier av en
 * erstatning er to kopier som kan gjøre den ulikt.
 */
export function fillContractTerms(
  template: string | null,
  names: { artist: string; organiser: string },
): string | null {
  if (template === null) return null;
  return template
    .replaceAll(CONTRACT_TERMS_TOKENS.artist, names.artist)
    .replaceAll(CONTRACT_TERMS_TOKENS.organiser, names.organiser);
}

/**
 * Standardteksten Atle ga, 4. september.
 *
 * HER OG IKKE BARE I EN MIGRERING. Basen har den som en kolonnedefault, men Drizzle sender et
 * eksplisitt null for kolonner den kjenner og som ikke står i objektet — så defaulten slår aldri
 * inn gjennom repositoryet. Den står derfor som en konstant i kontraktpakken, der BÅDE apiet (som
 * skriver den inn) og innstillingssiden (som kan tilby «tilbakestill») ser den samme teksten.
 *
 * Plassholderne fylles av `fillContractTerms`.
 */
export const DEFAULT_CONTRACT_TERMS =
  'Denne avtale gjelder for en opptreden med {{artist}} ved {{organiser}}. Oppfyllelsen av ' +
  'denne kontrakt skjer på grunnlag av gjensidig lojalitet mellom partene med et felles mål om ' +
  'å lage en best mulig konsert for alle involverte parter. Dette er en formelt bindende ' +
  'kontrakt som sammen med standardvilkår «Festivalvilkår norske artister 012019» danner ' +
  'kontrakten mellom partene. Vilkårene finner du på ' +
  'https://www.arrangor.no/media/2250163/festivalvilka-r-norske-artister-2019.pdf. ' +
  'Denne avtale er en bekreftelse på allerede inngåtte vilkår for opptreden. Denne avtale er ' +
  'gjort på grunnlag av oppgitt publikumskapasitet. Dersom kapasitet økes med mer enn 10%, må ' +
  'arrangør opplyse om dette og avtalen vurderes reforhandlet.';
/** Lengste sett vi tar imot: ti timer. Ikke en regel, en vakt mot en tastefeil. */
export const MAX_SET_LENGTH_MINUTES = 600;

/**
 * Flest gjestelisteplasser et bud eller en kontrakt kan avtale.
 *
 * HER OG IKKE I `performance-contract.ts`, selv om det er kontrakten som håndhever det i basen
 * (`ck_performance_contracts__seats`). Kontraktfila importerer denne; ville den andre veien vært
 * sant, hadde de to importert hverandre — og en sirkel mellom to moduler som begge bygger Zod-skjema
 * ved lasting er en `undefined` inne i en `.max()`, ikke en byggefeil.
 *
 * `MAX_CONTRACT_SEATS` er det samme tallet, eksportert derfra for de som allerede leser det navnet.
 */
export const MAX_GUEST_LIST_SEATS = 500;

const termText = z.string().trim().min(1).max(MAX_TERM_TEXT);

/** Én ytelse, som den ligger i basen: en part og en presisering. */
export const provisionTermSchema = z
  .object({ party: provisionPartySchema, note: z.string().nullable() })
  .meta({ id: 'ProvisionTerm' });
export type ProvisionTerm = z.infer<typeof provisionTermSchema>;

const provisionInput = z
  .object({ party: provisionPartySchema.optional(), note: termText.nullish() })
  .optional();

/**
 * ÉN BETALING I PLANEN.
 *
 * ANDEL OG IKKE BELØP. Endrer honoraret seg, holder «50 %» fortsatt; et lagret beløp ville blitt
 * feil uten at noe sa fra. Klienten regner kronene av honoraret og viser dem — den regningen er en
 * visning, ikke en sannhet som skal lagres to steder.
 */
export const bidPaymentSchema = z
  .object({
    id: z.uuid(),
    dueOn: z.iso.date(),
    sharePercent: z.number().int().min(1).max(100),
    note: z.string().nullable(),
  })
  .meta({ id: 'BidPayment' });
export type BidPayment = z.infer<typeof bidPaymentSchema>;

export const bidPaymentInputSchema = z
  .object({
    dueOn: z.iso.date(),
    sharePercent: z.number().int().min(1).max(100),
    note: z.string().trim().min(1).max(200).nullish(),
  })
  .meta({ id: 'BidPaymentInput' });
export type BidPaymentInput = z.infer<typeof bidPaymentInputSchema>;

/**
 * Vilkårene, slik de leses ut av et bud eller en kontrakt.
 *
 * ÉN FORM FOR BEGGE. Kontrakten arver den fra budet ved overføring, og en klient som kunne tegne
 * den ene kan tegne den andre.
 */
export const bidTermsSchema = z
  .object({
    performanceDate: z.iso.date().nullable(),
    /** `HH:MM`. Ikke sekunder: et bud er ikke en kjøreplan. */
    startsAt: z.string().nullable(),
    setLengthMinutes: z.number().int().positive().nullable(),
    /** «1 x 60 min» er `setCount: 1`, `setLengthMinutes: 60`. */
    setCount: z.number().int().positive(),
    billing: z.string().nullable(),
    capacity: z.number().int().nonnegative().nullable(),
    guestListSeats: z.number().int().nonnegative().nullable(),

    accommodation: provisionTermSchema,
    catering: provisionTermSchema,
    travel: provisionTermSchema,
    localTransport: provisionTermSchema,
    backline: provisionTermSchema,
    production: provisionTermSchema,

    exclusivityMode: exclusivityModeSchema,
    exclusivityRadiusKm: z.number().int().nonnegative().nullable(),
    exclusivityFrom: z.iso.date().nullable(),
    exclusivityTo: z.iso.date().nullable(),
    exclusivityText: z.string().nullable(),

    announcementText: z.string().nullable(),
    merchCommissionText: z.string().nullable(),
    attachmentsText: z.string().nullable(),
    deviationsText: z.string().nullable(),

    /**
     * VILKÅRSPUNKTET NEDERST, med artist og arrangør allerede satt inn.
     *
     * Atle: «Bruk denne teksten som default og med at den autogenerer artistnavn og arrangør …
     * Teksten kan redigeres når budet lages.»
     *
     * FERDIG UTFYLT OG IKKE EN MAL. Standarden i innstillingene bærer `{{artist}}` og
     * `{{organiser}}`; navnene settes inn når budet lages, og resultatet lagres. Et bud er et
     * dokument som er sendt — en tekst som fortsatt var en mal ville endret seg den dagen noen
     * rettet standarden, altså etter at avtalen var inngått.
     */
    contractTermsText: z.string().nullable(),
  })
  .meta({ id: 'BidTerms' });
export type BidTerms = z.infer<typeof bidTermsSchema>;

/** Alt av vilkår som kan skrives inn, alt valgfritt: et bud fylles ut litt om gangen. */
export const bidTermsInputSchema = z
  .object({
    performanceDate: z.iso.date().nullish(),
    startsAt: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .nullish(),
    setLengthMinutes: z.number().int().min(1).max(MAX_SET_LENGTH_MINUTES).nullish(),
    setCount: z.number().int().min(1).max(10).optional(),
    billing: z.string().trim().min(1).max(200).nullish(),
    capacity: z.number().int().min(0).max(1_000_000).nullish(),
    /*
     * DET SAMME TAKET SOM KONTRAKTEN, og det er ikke en detalj.
     *
     * `ck_performance_contracts__seats` stopper på 500. Budet tok tidligere ti tusen, og et bud med
     * 600 plasser ville gått fint helt til overføringen kopierte det — og da kastet MIDT i løkka,
     * etter at halve programmet var laget. Et tall som ikke overlever kopien er et tall skjemaet
     * ikke skal ta imot.
     */
    guestListSeats: z.number().int().min(0).max(MAX_GUEST_LIST_SEATS).nullish(),

    accommodation: provisionInput,
    catering: provisionInput,
    travel: provisionInput,
    localTransport: provisionInput,
    backline: provisionInput,
    production: provisionInput,

    exclusivityMode: exclusivityModeSchema.optional(),
    exclusivityRadiusKm: z.number().int().min(0).max(20_000).nullish(),
    exclusivityFrom: z.iso.date().nullish(),
    exclusivityTo: z.iso.date().nullish(),
    exclusivityText: termText.nullish(),

    announcementText: termText.nullish(),
    merchCommissionText: termText.nullish(),
    attachmentsText: termText.nullish(),
    deviationsText: termText.nullish(),
    /** Vilkårspunktet er lengre enn de andre — det er et avsnitt, ikke en setning. */
    contractTermsText: z.string().trim().min(1).max(MAX_CONTRACT_TERMS_TEXT).nullish(),

    /**
     * BETALINGSPLANEN SENDES HEL, ikke som enkeltrader.
     *
     * Andelene må summere til hundre, og det kan bare sjekkes når man ser alle. En rute som la til
     * én rad om gangen ville hatt en ugyldig plan mellom hvert kall, og ingen mulighet til å si fra.
     */
    payments: z.array(bidPaymentInputSchema).max(12).optional(),
  })
  .meta({ id: 'BidTermsInput' });
export type BidTermsInput = z.infer<typeof bidTermsInputSchema>;

/**
 * Organisasjonens standardvilkår — det innstillingssiden redigerer.
 *
 * Atle: «Standardtekst for budgivning kan ligge i innstillinger slik at hver org kan redigere
 * sitt.» Prinsipp 9: «Ingen andre offentlig annonserte opptredener i Salten sommeren 2026» er så
 * organisasjonsspesifikt det går an å bli, og hører ikke hjemme i koden.
 *
 * Et NYTT BUD fylles med disse. Deretter er de to uavhengige: å endre standarden skal ikke endre et
 * bud som allerede er sendt, og det er nettopp derfor budet har sine egne kolonner framfor å peke
 * hit.
 */
export const bookingTermDefaultsSchema = z
  .object({
    accommodation: provisionTermSchema,
    catering: provisionTermSchema,
    travel: provisionTermSchema,
    localTransport: provisionTermSchema,
    backline: provisionTermSchema,
    production: provisionTermSchema,

    exclusivityMode: exclusivityModeSchema,
    exclusivityRadiusKm: z.number().int().nonnegative().nullable(),
    exclusivityText: z.string().nullable(),

    announcementText: z.string().nullable(),
    merchCommissionText: z.string().nullable(),
    attachmentsText: z.string().nullable(),
    /** MALEN, med `{{artist}}` og `{{organiser}}` i seg. Se `BidTerms.contractTermsText`. */
    contractTermsText: z.string().nullable(),

    /**
     * Malene for henvendelser til byrået (migrering 0077).
     *
     * Atle, 8. september: «Denne automeldingen er fint om kan endres i innstillinger.» To maler
     * fordi det er to samtaler — en forespørsel spør om pris og tilgjengelighet før man har
     * bestemt seg, et bud er et tilbud på en bestemt dato. Plassholderne står i ENQUIRY_TOKENS.
     */
    enquiryTemplate: z.string().nullable(),
    bidTemplate: z.string().nullable(),

    setLengthMinutes: z.number().int().positive().nullable(),
    guestListSeats: z.number().int().nonnegative().nullable(),
  })
  .meta({ id: 'BookingTermDefaults' });
export type BookingTermDefaults = z.infer<typeof bookingTermDefaultsSchema>;

export const updateBookingTermDefaultsRequestSchema = z
  .object({
    accommodation: provisionInput,
    catering: provisionInput,
    travel: provisionInput,
    localTransport: provisionInput,
    backline: provisionInput,
    production: provisionInput,

    exclusivityMode: exclusivityModeSchema.optional(),
    exclusivityRadiusKm: z.number().int().min(0).max(20_000).nullish(),
    exclusivityText: termText.nullish(),

    announcementText: termText.nullish(),
    merchCommissionText: termText.nullish(),
    attachmentsText: termText.nullish(),
    contractTermsText: z.string().trim().min(1).max(MAX_CONTRACT_TERMS_TEXT).nullish(),

    /* Malene for henvendelser til byrået (0077). Se `ENQUIRY_TOKENS` for plassholderne. */
    enquiryTemplate: z.string().trim().min(1).max(4000).nullish(),
    bidTemplate: z.string().trim().min(1).max(4000).nullish(),

    setLengthMinutes: z.number().int().min(1).max(MAX_SET_LENGTH_MINUTES).nullish(),
    /** Samme tak som budet og kontrakten — se `bidTermsInputSchema`. */
    guestListSeats: z.number().int().min(0).max(MAX_GUEST_LIST_SEATS).nullish(),
  })
  .meta({ id: 'UpdateBookingTermDefaultsRequest' });
export type UpdateBookingTermDefaultsRequest = z.infer<
  typeof updateBookingTermDefaultsRequestSchema
>;

/**
 * HVORFOR ET BUD ER SPERRET, som en maskinlesbar grunn framfor en setning.
 *
 * Atle: «gjør dette intuitivt, slik at man har beskjed til brukeren på hvorfor bud er blokkert.»
 * APIET sier HVA som mangler; klienten skriver setningen på leserens språk og kan lenke rett til
 * stedet der man retter det.
 */
export const BID_BLOCKERS = ['not_placed', 'no_edition', 'no_fee'] as const;
export const bidBlockerSchema = z.enum(BID_BLOCKERS).meta({ id: 'BidBlocker' });
export type BidBlocker = (typeof BID_BLOCKERS)[number];

/** Hvor artisten står i dag, og hva som eventuelt hindrer et bud. */
export const artistPlacementSchema = z
  .object({
    draftItemId: z.uuid(),
    draftId: z.uuid(),
    draftName: z.string(),
    editionId: z.uuid(),
    editionName: z.string(),
    /** Festivalen eller spillestedet årgangen hører til. */
    festivalId: z.uuid(),
    festivalName: z.string(),
    /** `festival` eller `venue` — det samme skillet `festivals.kind` gjør. */
    festivalKind: z.string(),
    stageId: z.uuid(),
    stageName: z.string(),
    stageCapacity: z.number().int().nonnegative().nullable(),
    day: z.iso.date(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    /** Om det allerede står et bud på denne plasseringen. */
    hasBid: z.boolean(),
  })
  .meta({ id: 'ArtistPlacement' });
export type ArtistPlacement = z.infer<typeof artistPlacementSchema>;

/**
 * EN SAL I ET SPILLESTED, SOM PLASSERING (sak 230, 0455).
 *
 * Et hus har ikke noe brett og salene er ingen scener, så plasseringen er «sal + dato»: salen
 * herfra, datoen velger den som byr. Ingen årgang — et spillested har ingen i brukerflaten.
 */
export const venuePlacementSchema = z
  .object({
    venueRoomId: z.uuid(),
    /** Spillestedet (festivalraden med `kind = 'venue'`). */
    festivalId: z.uuid(),
    venueName: z.string(),
    roomName: z.string(),
    capacity: z.number().int().nonnegative().nullable(),
  })
  .meta({ id: 'VenuePlacement' });
export type VenuePlacement = z.infer<typeof venuePlacementSchema>;

/**
 * EN FESTIVALÅRGANG ARTISTEN KAN BYS PÅ UTEN Å STÅ I PROGRAMMET FØRST (QA15 B4): budet velger dag og
 * scene, og API-et legger artisten i programmet (`placeOn`). Bare årganger som ikke er over.
 */
export const editionPlacementSchema = z
  .object({
    editionId: z.uuid(),
    editionName: z.string(),
    festivalId: z.uuid(),
    festivalName: z.string(),
    startsOn: z.iso.date().nullable(),
    endsOn: z.iso.date().nullable(),
    /** Årgangen har et program (utkast) fra før; ellers lages det med budet. */
    hasProgramme: z.boolean(),
    stages: z.array(
      z.object({ id: z.uuid(), name: z.string(), capacity: z.number().int().nonnegative().nullable() }),
    ),
  })
  .meta({ id: 'EditionPlacement' });
export type EditionPlacement = z.infer<typeof editionPlacementSchema>;

export const artistPlacementsResponseSchema = z
  .object({
    items: z.array(artistPlacementSchema),
    /** Salene i husets spillesteder — «sal + dato» (sak 230). Tom for et hus uten saler. */
    rooms: z.array(venuePlacementSchema),
    /** QA15 B4: festivalårgangene artisten kan bys på direkte. Valgfri for eldre klienter. */
    editions: z.array(editionPlacementSchema).default([]),
    /** Tom når artisten kan få et bud. Ellers grunnen, som klienten skriver ut. */
    blockedBy: bidBlockerSchema.nullable(),
  })
  .meta({ id: 'ArtistPlacementsResponse' });
export type ArtistPlacementsResponse = z.infer<typeof artistPlacementsResponseSchema>;

/**
 * SALENS KØ PÅ EN DATO, slik budmodalen viser den (QA15 C3): «1. hold på salen: Nordvind», og hva
 * budet blir når det sendes. Bare husets egne saler; kalenderen og konfliktsjekken leser den samme
 * køen (`venue_enquiries`, 1212).
 */
export const roomDateQueueEntrySchema = z
  .object({
    id: z.uuid(),
    /** `night` er et arrangement på datoen; de andre er køens rader. */
    kind: z.enum(['night', 'confirmed', 'hold', 'waiting']),
    rank: z.number().int().min(1).max(3).nullable(),
    /** Datoforespørsel fra en arrangør, eller husets eget bud. */
    source: z.enum(['date_request', 'booking_bid', 'night']),
    who: z.string().nullable(),
    artistName: z.string().nullable(),
    /**
     * Hva raden gjør med datoen, fra den samme regelen markedet og husets kalender bruker
     * (`app.venue_room_occupancy`, QA15-S I3): opptatt, hold, eller venter (tar ikke datoen).
     */
    occupancy: z.enum(['booked', 'hold', 'waiting']),
    /** Arrangementets trinn på stigen (`eventStatus.*`); null for køens rader. */
    eventStatus: z.string().nullable(),
    /** QA15-S K2: the organisation that asked (a date request), so a lease to them can answer it. */
    requesterOrganizationId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'RoomDateQueueEntry' });
export type RoomDateQueueEntry = z.infer<typeof roomDateQueueEntrySchema>;

export const roomDateQueueSchema = z
  .object({
    entries: z.array(roomDateQueueEntrySchema),
    /** Plassen et nytt bud får: 1–3, eller null når alle tre holdene er tatt (står i køen). */
    nextRank: z.number().int().min(1).max(3).nullable(),
    /** Noe på datoen er opptatt (bekreftet arrangement eller bekreftet køplass). */
    taken: z.boolean(),
  })
  .meta({ id: 'RoomDateQueue' });
export type RoomDateQueue = z.infer<typeof roomDateQueueSchema>;

/** Taket på et honorar, det samme som resten av produktet bruker. */
export const MAX_BID_AMOUNT_MINOR = MAX_COST_AMOUNT_MINOR;

// ------------------------------------------------------------ meldingen til byrået

/**
 * Plassholderne en henvendelsesmal kan bruke.
 *
 * Atle, 8. september, med sitt eget utkast:
 *
 *     Hei,
 *     Har du mulighet til å gi meg asking fee og tilgjenglighet for ARTIST den DATO i STED.
 *
 *     Vennlig hilsen
 *     NAVN
 *     ORGANISASJON
 *
 * Fire av dem er hans; `agent` kom til fordi en melding som åpner med «Hei,» leser dårligere enn
 * en som åpner med «Hei Lasse,» — og navnet står allerede på byrået.
 *
 * DATO OG STED ER VALGFRIE, og det er selve poenget med en forespørsel: man spør ofte FØR man har
 * bestemt datoen. Står de tomme, faller setningen om dem bort framfor å bli «den  i ».
 */
export const ENQUIRY_TOKENS = {
  artist: '{{artist}}',
  date: '{{date}}',
  venue: '{{venue}}',
  sender: '{{sender}}',
  organisation: '{{organisation}}',
  agent: '{{agent}}',
  amount: '{{amount}}',
  /**
   * STOERRELSEN FORESPOERSELEN GJELDER (QA runde 5, punkt 15).
   *
   * Rapporten: «Foresporselsteksten nevner ikke formatet man har valgt.» Byraaet leser en tekst om
   * en dato og en pris og maa gjette om det er trioen eller det fulle bandet — og de to er to
   * ulike varer (SS5.2). Velgeren kom 11. september; setningen fulgte ikke med.
   *
   * Faller bort til ingenting naar ingen stoerrelse er valgt, som de andre.
   */
  format: '{{format}}',
  /**
   * KLOKKESLETT, SCENE OG BILLING (QA-punkt 25).
   *
   * Rapporten: «Budmeldingen mangler klokkeslett, scene og billing.» De tre er det agenten
   * leser for å vite om budet er interessant i det hele tatt — en 22:00-slot på hovedscenen
   * som headliner er et annet tilbud enn 14:00 på klubbscenen som support, til samme pris.
   *
   * Malen er en INNSTILLING per organisasjon, så nye plassholdere dukker bare opp der noen tar
   * dem i bruk. Standardmalen under gjør det; en organisasjon som har skrevet sin egen beholder
   * sin, og de tre nye faller bort til ingenting slik de andre gjør.
   */
  time: '{{time}}',
  stage: '{{stage}}',
  billing: '{{billing}}',
} as const;

export interface EnquiryValues {
  artist: string;
  date: string | null;
  time?: string | null;
  stage?: string | null;
  billing?: string | null;
  /** Stoerrelsen foresporselen gjelder (QA runde 5, punkt 15). Faller bort naar ingen er valgt. */
  format?: string | null;
  venue: string | null;
  sender: string;
  organisation: string;
  agent: string | null;
  /**
   * Bare budet har en sum. Feltet er valgfritt fordi en forespørsel per definisjon ikke har en —
   * og en mal som likevel nevner `{{amount}}` skal da miste ordet, ikke vise plassholderen.
   */
  amount?: string | null;
}

/**
 * HVEM HILSENEN GJELDER (QA15 funn 12): fornavnet til bookingkontakten for artisten, «Hei Siri,».
 *
 * Byrånavnet («Hei Standing Ovation,») er aldri en hilsen. Er kontakten byråets generelle
 * (post@, sentralbord) eller ukjent, blir det null, og malen gir den nøytrale «Hei,».
 */
export function enquiryGreetingName(
  contact: { readonly name: string; readonly general: boolean } | null | undefined,
): string | null {
  if (contact == null || contact.general) return null;
  const first = contact.name.trim().split(/\s+/)[0] ?? '';
  return first === '' ? null : first;
}

/**
 * Malen med verdiene satt inn.
 *
 * ÉN FUNKSJON, som `fillContractTerms`, fordi både forhåndsvisningen i innstillingene og skjemaet
 * som faktisk sender må gjøre nøyaktig det samme.
 *
 * En tom dato eller et tomt sted blir til ingenting, ikke til en tom plassholder: «for Karpe den
 * {{date}} i {{venue}}» er verre enn «for Karpe», fordi den ser ut som en feil mottakeren skal
 * tolke.
 */
export function fillEnquiry(template: string | null, values: EnquiryValues): string | null {
  if (template === null) return null;

  const filled: Readonly<Record<keyof typeof ENQUIRY_TOKENS, string>> = {
    artist: values.artist,
    sender: values.sender,
    organisation: values.organisation,
    agent: values.agent ?? '',
    date: values.date ?? '',
    venue: values.venue ?? '',
    amount: values.amount ?? '',
    time: values.time ?? '',
    stage: values.stage ?? '',
    billing: values.billing ?? '',
    format: values.format ?? '',
  };

  /**
   * EN LINJE SOM BARE VAR EN PLASSHOLDER FALLER BORT — HELE LINJA (QA Kapling!).
   *
   * ================================================================================================
   * LØFTET STO I MALEN, OG KODEN HOLDT DET IKKE.
   *
   * Over `Størrelse: {{format}}` sto det: «Linja faller bort til ingenting når ingen størrelse er
   * plukket — `fillEnquiry` rydder etter seg.» Det gjorde den ikke. Oppryddingen under klemmer
   * MELLOMROM; den fjerner ingen linjer. Et byrå fikk derfor en melding som endte på en naken
   * «Størrelse:» uten noe bak. Det samme løftet står over `Scene:`, `Tid:` og `Billing:` i
   * budmalen, med den samme feilen.
   *
   * ================================================================================================
   * REGELEN ER SMAL MED VILJE: linja må HA HATT en plassholder, og ALLE plassholderne på den må ha
   * blitt tomme.
   *
   * Den enkle varianten — «slett linjer som bare er en etikett og et kolon» — ville slettet en
   * overskrift brukeren selv har skrevet over sin egen fritekst. Malen er redigerbar, så det er
   * ikke et påfunnet tilfelle.
   *
   * En linje der ETT felt er fylt og et annet ikke — «... {{artist}} den {{date}} i {{venue}}» med
   * bare artisten — blir stående, og mellomromsoppryddingen under tar seg av den som før. Det er
   * nettopp den oppførselen som var riktig fra før, og den skal ikke endres.
   *
   * OG LINJA MÅ VÆRE EN ETIKETT. Første utgave droppet hver linje der alle plassholderne ble tomme,
   * og tok «Hei {{agent}},» med seg — en egen test fanget det. En hilsen uten navn er fortsatt en
   * hilsen: «Hei,» er riktig, og den skal stå. Et FELT uten verdi er derimot ingenting, og et felt
   * kjennes på kolonet: det som blir igjen er «Størrelse:» og ikke et ord til.
   */
  const body = template
    .split('\n')
    .filter((line) => {
      const tokens = Object.entries(filled).filter(([key]) =>
        line.includes(ENQUIRY_TOKENS[key as keyof typeof ENQUIRY_TOKENS]),
      );
      if (tokens.length === 0) return true;
      if (tokens.some(([, value]) => value !== '')) return true;

      /* Alle plassholderne ble tomme. Er resten bare en etikett, var linja et felt. */
      let bare = line;
      for (const key of Object.keys(filled)) {
        bare = bare.replaceAll(ENQUIRY_TOKENS[key as keyof typeof ENQUIRY_TOKENS], '');
      }
      return !/^[^:]{1,40}:$/.test(bare.trim());
    })
    .join('\n');

  return (
    body
      .replaceAll(ENQUIRY_TOKENS.artist, filled.artist)
      .replaceAll(ENQUIRY_TOKENS.sender, filled.sender)
      .replaceAll(ENQUIRY_TOKENS.organisation, filled.organisation)
      .replaceAll(ENQUIRY_TOKENS.agent, filled.agent)
      .replaceAll(ENQUIRY_TOKENS.date, filled.date)
      .replaceAll(ENQUIRY_TOKENS.venue, filled.venue)
      .replaceAll(ENQUIRY_TOKENS.amount, filled.amount)
      .replaceAll(ENQUIRY_TOKENS.time, filled.time)
      .replaceAll(ENQUIRY_TOKENS.stage, filled.stage)
      .replaceAll(ENQUIRY_TOKENS.billing, filled.billing)
      .replaceAll(ENQUIRY_TOKENS.format, filled.format)
      /*
       * Rydder etter plassholdere som falt bort: «den  i », «Hei ,».
       *
       * SPØRSMÅLSTEGNET ER MED I TEGNKLASSEN SIDEN 19. SEPTEMBER (30c). Forespørselsmalen ender nå
       * med «?» framfor «.», og uten tegnet her ville «for Karpe den  i ?» blitt stående halv —
       * oppryddingen ville ikke sett at setningen var slutt. Tre tegn i én klasse framfor tre
       * regler: det er den samme opprydningen, og et fjerde tegn skal ikke kreve en fjerde linje.
       */
      .replaceAll(/ +den +(?= |[.,?])/g, ' ')
      .replaceAll(/ +i +(?= |[.,?])/g, ' ')
      .replaceAll(/ {2,}/g, ' ')
      .replaceAll(/ +([.,?])/g, '$1')
      /* En fjernet linje etterlot seg tre linjeskift der det skulle vært ett avsnittsskille. */
      .replaceAll(/\n{3,}/g, '\n\n')
  );
}

/**
 * Atles egen tekst, 8. september. Standarden en organisasjon får før noen rører innstillingen.
 *
 * -------------------------------------------------------------------------------------------
 * TO RETTELSER 19. SEPTEMBER (QA-runde 12, 30c og J8).
 *
 *  - **Spørsmålstegnet.** «Har du mulighet til å gi meg … .» er et spørsmål med punktum. Atle
 *    meldte det som «språkfeil i standardmalen», og det er den feilen: setningen SPØR, og en
 *    forespørsel som ikke ser ut som et spørsmål er en forespørsel folk glemmer å svare på.
 *  - **«asking fee» → «honorar».** J8 gjør «Honorar» til det ene ordet for artistens fee, og
 *    pakke J byttet hver synlige forekomst. Malen er også en synlig tekst — den er det som
 *    faktisk GÅR ut av huset — og var den siste som sto igjen på engelsk.
 */
export const DEFAULT_ENQUIRY_TEMPLATE =
  'Hei {{agent}},\n\n' +
  'Har du mulighet til å gi meg honorar og tilgjengelighet for {{artist}} den {{date}} i {{venue}}?\n\n' +
  /*
   * STØRRELSEN, NÅR EN ER VALGT (QA runde 5, punkt 15; rettet etter QA Kapling!).
   *
   * HELE linja faller bort når ingen størrelse er plukket, så en forespørsel om «alle størrelser»
   * leser nøyaktig som før. Fram til Kapling!-runden sto det samme løftet her uten at `fillEnquiry`
   * holdt det — se regelen der.
   */
  'Størrelse: {{format}}\n\n' +
  'Vennlig hilsen\n{{sender}}\n{{organisation}}';

/**
 * Budets mal. Samme form, annet ærend: her er datoen bestemt og beløpet nevnt.
 *
 * `{{amount}}` finnes bare her — en forespørsel har per definisjon ingen sum.
 */
export const DEFAULT_BID_TEMPLATE =
  'Hei {{agent}},\n\n' +
  'Vi vil gjerne ha {{artist}} til {{venue}} den {{date}}, og tilbyr {{amount}}.\n\n' +
  /*
   * De tre linjene under faller bort til ingenting når feltene er tomme — `fillEnquiry` rydder
   * etter seg. Et bud uten scene eller klokkeslett leser derfor som før (QA-punkt 25).
   */
  'Scene: {{stage}}\n' +
  'Tid: {{time}}\n' +
  'Billing: {{billing}}\n\n' +
  'Si fra om dere trenger flere detaljer, så sender vi over.\n\n' +
  'Vennlig hilsen\n{{sender}}\n{{organisation}}';
